import { environment } from "../../config/environment";
import React, { useState, useRef, useEffect } from "react";
import Sidebar from "../Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { customFetch } from "../../utils/Auth";
import HighlightText from "../common/HighlightText";
const medCsvUrl = (process.env.PUBLIC_URL || "") + "/artifacts/rx-dataset-simple.csv";

const TAB_MEDICAL_ENTITY = "medical_entity";
const TAB_DIAGNOSIS_INFERENCE = "diagnosis_inference";
const TAB_MEDICAL_ENTITY_BY_CODE = "medical_entity_by_code";
const TAB_CONSUMER_HALLUCINATIONS = "consumer_hallucinations";

const Hallucination = ({ setAuth, setUserOrg }) => {
  const [activeTab, setActiveTab] = useState(TAB_MEDICAL_ENTITY);
  const [patientId, setPatientId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [layerSelection, setLayerSelection] = useState({ transcript: 'omission', note: 'hallucination' });
  const [consumerDataTab, setConsumerDataTab] = useState("medications");
  const [consumerFocusedGroup, setConsumerFocusedGroup] = useState(null); // { pane, category, key }
  const [consumerOccurrenceIndexByGroup, setConsumerOccurrenceIndexByGroup] = useState({});
  const [medNames, setMedNames] = useState([]);
  const medIndexRef = useRef({ byFirst: new Map(), byFirst2: new Map(), entries: [] });

  // Load medication dataset on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(medCsvUrl);
        const text = await res.text();
        const lines = text.split(/\r?\n/).filter(l => l && l.trim());
        if (lines.length === 0) return;
        const header = lines[0].split(",");
        const nameIdx = header.findIndex(h => /^(name|drug|rxnorm_name|term|text|label)$/i.test(h.trim()));
        const idx = nameIdx >= 0 ? nameIdx : 0;
        const names = new Set();
        for (let i = 1; i < lines.length; i++) {
          const parts = lines[i].split(",");
          const val = (parts[idx] || "").trim();
          if (val) names.add(val);
        }
        if (!cancelled) {
          const arr = Array.from(names);
          setMedNames(arr);
          // build simple prefix index
          const byFirst = new Map();
          const byFirst2 = new Map();
          const entries = arr.map(name => ({ name, lower: name.toLowerCase() }));
          for (const e of entries) {
            const f1 = e.lower.charAt(0);
            const f2 = e.lower.slice(0, 2);
            if (f1) {
              if (!byFirst.has(f1)) byFirst.set(f1, []);
              byFirst.get(f1).push(e);
            }
            if (f2 && f2.length === 2) {
              if (!byFirst2.has(f2)) byFirst2.set(f2, []);
              byFirst2.get(f2).push(e);
            }
          }
          medIndexRef.current = { byFirst, byFirst2, entries };
        }
      } catch (_) {
        // ignore load errors
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const levenshtein = (a, b) => {
    const s = a.toLowerCase();
    const t = b.toLowerCase();
    const m = s.length, n = t.length;
    if (m === 0) return n; if (n === 0) return m;
    const dp = new Array(n + 1);
    for (let j = 0; j <= n; j++) dp[j] = j;
    for (let i = 1; i <= m; i++) {
      let prev = i - 1;
      dp[0] = i;
      for (let j = 1; j <= n; j++) {
        const temp = dp[j];
        if (s[i - 1] === t[j - 1]) dp[j] = prev; else dp[j] = Math.min(prev + 1, dp[j] + 1, dp[j - 1] + 1);
        prev = temp;
      }
    }
    return dp[n];
  };

  const getMedicationSuggestions = (query) => {
    const q = (query || "").trim();
    if (!q || medIndexRef.current.entries.length === 0) return [];
    const ql = q.toLowerCase();
    const { byFirst, byFirst2, entries } = medIndexRef.current;
    const set = new Set();
    const candidates = [];
    const add = (e) => { const key = e.name; if (!set.has(key)) { set.add(key); candidates.push(e); } };

    // 1) substring matches (case-insensitive)
    for (const e of entries) {
      if (e.lower.includes(ql)) add(e);
      if (candidates.length >= 1000) break;
    }
    // 2) same first 2 letters bucket
    const b2 = byFirst2.get(ql.slice(0, 2));
    if (b2) for (const e of b2) add(e);
    // 3) same first letter bucket
    const b1 = byFirst.get(ql.charAt(0));
    if (b1) for (const e of b1) add(e);
    // 4) fallback: sample more if still small
    if (candidates.length < 200) {
      for (let i = 0; i < entries.length && candidates.length < 2000; i++) add(entries[i]);
    }

    const maxLenQ = Math.max(1, ql.length);
    const scored = candidates.map(e => {
      const d = levenshtein(ql, e.lower);
      const score = Math.max(0, 1 - d / Math.max(maxLenQ, e.lower.length));
      return { name: e.name, score: Number(score.toFixed(3)) };
    });
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 5);
  };

  const normalizeMedicationLabel = (value) =>
    String(value || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, " ");

  const hasPerfectMedicationSuggestion = (label) => {
    const normalizedLabel = normalizeMedicationLabel(label);
    if (!normalizedLabel) return false;
    const suggestions = getMedicationSuggestions(label);
    return suggestions.some(s => normalizeMedicationLabel(s?.name) === normalizedLabel || Number(s?.score) === 1);
  };

  const splitFlaggedMedicationItems = (items) => {
    const perfectMatchItems = [];
    const otherItems = [];
    for (const item of items || []) {
      if (hasPerfectMedicationSuggestion(item?.label)) {
        perfectMatchItems.push(item);
      } else {
        otherItems.push(item);
      }
    }
    return { perfectMatchItems, otherItems };
  };

  const normalizeConsumerGroupKey = (value) =>
    String(value || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, " ");

  const getConsumerDisplayLabel = (value, fallback = "Unlabeled") => {
    const text = String(value || "").trim();
    return text || fallback;
  };

  const groupConsumerOccurrences = (items, getLabel, getStart, getEnd) => {
    const map = new Map();
    for (const item of items || []) {
      const label = getConsumerDisplayLabel(getLabel(item), "Unlabeled");
      const key = normalizeConsumerGroupKey(label);
      const start = Number(getStart(item));
      const end = Number(getEnd(item));
      if (!map.has(key)) {
        map.set(key, { key, label, occurrences: [] });
      }
      map.get(key).occurrences.push({
        start: Number.isFinite(start) ? start : 0,
        end: Number.isFinite(end) ? end : 0,
        item
      });
    }
    const groups = Array.from(map.values());
    for (const group of groups) {
      group.occurrences.sort((a, b) => a.start - b.start);
    }
    groups.sort((a, b) => {
      const sa = a.occurrences[0]?.start ?? 0;
      const sb = b.occurrences[0]?.start ?? 0;
      return sa - sb;
    });
    return groups;
  };

  useEffect(() => {
    setLayerSelection({ transcript: 'omission', note: 'hallucination' });
  }, [results, selectedIndex]);

  useEffect(() => {
    setConsumerFocusedGroup(null);
    setConsumerOccurrenceIndexByGroup({});
  }, [results, selectedIndex, activeTab, consumerDataTab]);

  // Shared refs (only one item is open at a time)
  const transcriptRef = useRef(null);
  const noteRef = useRef(null);

  const getCommonItems = (comparison) => {
    const items = [];
    if (!comparison) return items;
    const entityCommon = Array.isArray(comparison?.entity?.common) ? comparison.entity.common : [];
    const conceptCommon = Array.isArray(comparison?.concept?.common) ? comparison.concept.common : [];

    // Track entity transcript ranges for suppression checks
    const entityTranscriptRanges = [];

    for (const item of entityCommon) {
      const transcriptStart = item?.TranscriptBeginOffset ?? null;
      const transcriptEnd = item?.TranscriptEndOffset ?? null;
      if (typeof transcriptStart === "number" && typeof transcriptEnd === "number") {
        entityTranscriptRanges.push([transcriptStart, transcriptEnd]);
      }
      items.push({
        transcriptStart,
        transcriptEnd,
        noteStart: item?.NoteBeginOffset ?? null,
        noteEnd: item?.NoteEndOffset ?? null,
        transcriptLabel: item?.TrancriptText ?? item?.TranscriptText ?? "",
        noteLabel: item?.NoteText ?? "",
        kind: "entity",
        raw: item,
      });
    }

    for (const item of conceptCommon) {
      const transcriptStart = item?.TranscriptBeginOffset ?? null;
      const transcriptEnd = item?.TranscriptEndOffset ?? null;

      // Suppress concept item if its transcript range is fully contained within any entity transcript range
      let suppressed = false;
      if (typeof transcriptStart === "number" && typeof transcriptEnd === "number") {
        suppressed = entityTranscriptRanges.some(([s, e]) =>
          typeof s === "number" && typeof e === "number" && transcriptStart >= s && transcriptEnd <= e
        );
      }

      if (suppressed) continue;

      items.push({
        transcriptStart,
        transcriptEnd,
        noteStart: item?.NoteBeginOffset ?? null,
        noteEnd: item?.NoteEndOffset ?? null,
        transcriptLabel: item?.TrancriptText ?? item?.TranscriptText ?? "",
        noteLabel: item?.NoteText ?? "",
        kind: "concept",
        raw: item,
      });
    }

    return items;
  };

  const getOmissionItems = (comparison) => {
    const items = [];
    if (!comparison) return items;
    const entityOmissions = Array.isArray(comparison?.entity?.omissions) ? comparison.entity.omissions : [];
    const conceptOmissions = Array.isArray(comparison?.concept?.omissions) ? comparison.concept.omissions : [];

    for (const item of entityOmissions) {
      items.push({
        transcriptStart: item?.TranscriptBeginOffset ?? null,
        transcriptEnd: item?.TranscriptEndOffset ?? null,
        transcriptLabel: item?.TrancriptText ?? item?.TranscriptText ?? "",
        kind: "entity_omission",
        raw: item,
      });
    }
    for (const item of conceptOmissions) {
      items.push({
        transcriptStart: item?.TranscriptBeginOffset ?? null,
        transcriptEnd: item?.TranscriptEndOffset ?? null,
        transcriptLabel: item?.TrancriptText ?? item?.TranscriptText ?? "",
        kind: "concept_omission",
        raw: item,
      });
    }
    return items.filter(it => typeof it.transcriptStart === "number" && typeof it.transcriptEnd === "number");
  };

  const getHallucinationItems = (comparison) => {
    const items = [];
    if (!comparison) return items;
    const entityHall = Array.isArray(comparison?.entity?.hallucinations) ? comparison.entity.hallucinations : [];
    const conceptHall = Array.isArray(comparison?.concept?.hallucinations) ? comparison.concept.hallucinations : [];

    for (const item of entityHall) {
      items.push({
        noteStart: item?.NoteBeginOffset ?? null,
        noteEnd: item?.NoteEndOffset ?? null,
        noteLabel: item?.NoteText ?? "",
        kind: "entity_hallucination",
        raw: item,
      });
    }
    for (const item of conceptHall) {
      items.push({
        noteStart: item?.NoteBeginOffset ?? null,
        noteEnd: item?.NoteEndOffset ?? null,
        noteLabel: item?.NoteText ?? "",
        kind: "concept_hallucination",
        raw: item,
      });
    }
    return items.filter(it => typeof it.noteStart === "number" && typeof it.noteEnd === "number");
  };

  // Build synthetic comparison from rxnorm_medications_by_code_result (by_rxnorm_code shape)
  // Shape: { by_rxnorm_code, common, hallucinated, omitted } — each element has note_mentions[], transcript_mentions[] with begin_offset, end_offset, text
  const getComparisonFromByCodeResult = (byCodeResult) => {
    try {
      const parsed = typeof byCodeResult === "string" ? JSON.parse(byCodeResult) : byCodeResult || {};
      const commonList = Array.isArray(parsed.common) ? parsed.common : [];
      const hallucinatedList = Array.isArray(parsed.hallucinated) ? parsed.hallucinated : [];
      const omittedList = Array.isArray(parsed.omitted) ? parsed.omitted : [];

      const toCommonPairs = (list) => {
        const pairs = [];
        for (const entry of list) {
          const notes = Array.isArray(entry.note_mentions) ? entry.note_mentions : [];
          const transcripts = Array.isArray(entry.transcript_mentions) ? entry.transcript_mentions : [];
          const n = Math.max(notes.length, transcripts.length);
          for (let i = 0; i < n; i++) {
            const nm = notes[i];
            const tm = transcripts[i];
            pairs.push({
              TranscriptBeginOffset: tm?.begin_offset,
              TranscriptEndOffset: tm?.end_offset,
              NoteBeginOffset: nm?.begin_offset,
              NoteEndOffset: nm?.end_offset,
              TrancriptText: tm?.text ?? "",
              NoteText: nm?.text ?? "",
              raw: entry,
            });
          }
        }
        return pairs;
      };

      const toOmissions = (list) => {
        const items = [];
        for (const entry of list) {
          const transcripts = Array.isArray(entry.transcript_mentions) ? entry.transcript_mentions : [];
          for (const tm of transcripts) {
            items.push({
              TranscriptBeginOffset: tm?.begin_offset,
              TranscriptEndOffset: tm?.end_offset,
              TrancriptText: tm?.text ?? "",
              raw: entry,
            });
          }
        }
        return items;
      };

      const toHallucinations = (list) => {
        const items = [];
        for (const entry of list) {
          const notes = Array.isArray(entry.note_mentions) ? entry.note_mentions : [];
          for (const nm of notes) {
            items.push({
              NoteBeginOffset: nm?.begin_offset,
              NoteEndOffset: nm?.end_offset,
              NoteText: nm?.text ?? "",
              raw: entry,
            });
          }
        }
        return items;
      };

      return {
        entity: {
          common: toCommonPairs(commonList),
          omissions: toOmissions(omittedList),
          hallucinations: toHallucinations(hallucinatedList),
        },
        concept: { common: [], omissions: [], hallucinations: [] },
      };
    } catch {
      return { entity: { common: [], omissions: [], hallucinations: [] }, concept: { common: [], omissions: [], hallucinations: [] } };
    }
  };

  const extractMedicationHighlights = (analysis, kind) => {
    // kind: "note" | "transcript" — medical_entity_analysis_result shape
    try {
      const parsed = typeof analysis === "string" ? JSON.parse(analysis) : analysis || {};
      const list = Array.isArray(parsed?.medication) ? parsed.medication : [];
      const bucket = list.find(x => (x?.type || "").toLowerCase() === kind);
      const flagged = Array.isArray(bucket?.medication_flagged) ? bucket.medication_flagged : [];
      return flagged.map(item => ({
        start: item?.BeginOffset ?? item?.begin ?? 0,
        end: item?.EndOffset ?? item?.end ?? 0,
        data: item
      }));
    } catch {
      return [];
    }
  };

  // diagnosis_entities_analysis_result shape: diagnosis[] with type "note"|"transcript", diagnosis_flagged with Note*/Transcript* fields
  const extractDiagnosisHighlights = (analysis, kind) => {
    try {
      const parsed = typeof analysis === "string" ? JSON.parse(analysis) : analysis || {};
      const list = Array.isArray(parsed?.diagnosis) ? parsed.diagnosis : [];
      const bucket = list.find(x => (x?.type || "").toLowerCase() === kind);
      const flagged = Array.isArray(bucket?.diagnosis_flagged) ? bucket.diagnosis_flagged : [];
      if (kind === "note") {
        return flagged.map(item => ({
          start: item?.NoteBeginOffset ?? item?.BeginOffset ?? item?.begin ?? 0,
          end: item?.NoteEndOffset ?? item?.EndOffset ?? item?.end ?? 0,
          data: {
            ...item,
            Text: item?.NoteText
              ?? item?.note_text
              ?? item?.NoteDiagnosis
              ?? item?.DiagnosisText
              ?? item?.diagnosis_text
              ?? item?.Diagnosis
              ?? item?.diagnosis
              ?? item?.text
              ?? item?.Text,
            text: item?.NoteText
              ?? item?.note_text
              ?? item?.NoteDiagnosis
              ?? item?.DiagnosisText
              ?? item?.diagnosis_text
              ?? item?.Diagnosis
              ?? item?.diagnosis
              ?? item?.text
              ?? item?.Text
          }
        }));
      }
      return flagged.map(item => ({
        start: item?.TranscriptBeginOffset ?? item?.BeginOffset ?? item?.begin ?? 0,
        end: item?.TranscriptEndOffset ?? item?.EndOffset ?? item?.end ?? 0,
        data: {
          ...item,
          Text: item?.TranscriptText
            ?? item?.transcript_text
            ?? item?.TranscriptDiagnosis
            ?? item?.DiagnosisText
            ?? item?.diagnosis_text
            ?? item?.Diagnosis
            ?? item?.diagnosis
            ?? item?.text
            ?? item?.Text,
          text: item?.TranscriptText
            ?? item?.transcript_text
            ?? item?.TranscriptDiagnosis
            ?? item?.DiagnosisText
            ?? item?.diagnosis_text
            ?? item?.Diagnosis
            ?? item?.diagnosis
            ?? item?.text
            ?? item?.Text
        }
      }));
    } catch {
      return [];
    }
  };

  const getFlaggedHighlights = (analysisResult, kind, analysisTab = activeTab) => {
    if (analysisTab === TAB_DIAGNOSIS_INFERENCE) {
      return extractDiagnosisHighlights(analysisResult, kind);
    }
    return extractMedicationHighlights(analysisResult, kind);
  };

  // Build a readable label for a flagged highlight; avoid showing "0-0" when text and offsets are missing
  const getFlaggedHighlightLabel = (h) => {
    const text = h?.data?.Text
      ?? h?.data?.text
      ?? h?.data?.NoteText
      ?? h?.data?.TranscriptText
      ?? h?.data?.NoteDiagnosis
      ?? h?.data?.TranscriptDiagnosis
      ?? h?.data?.DiagnosisText
      ?? h?.data?.diagnosis_text
      ?? h?.data?.Diagnosis
      ?? h?.data?.diagnosis
      ?? h?.data?.ConceptDescription
      ?? "";
    if (text && String(text).trim()) return String(text).trim();
    const start = h?.start ?? 0;
    const end = h?.end ?? 0;
    if (start === 0 && end === 0) return "Unlabeled flagged item";
    return `${start}-${end}`;
  };

  // For Medical Entity tab: normalize entity_common / concept_omission etc. to the layer name HighlightText expects
  const normalizeVisibleLayer = (layer) => {
    if (typeof layer !== "string") return layer;
    if (layer === "entity_common" || layer === "concept_common") return "common";
    if (layer === "entity_omission" || layer === "concept_omission") return "omission";
    if (layer === "entity_hallucination" || layer === "concept_hallucination") return "hallucination";
    return layer;
  };

  const formatAnalysis = (value) => {
    try {
      if (typeof value === "string") {
        return JSON.stringify(JSON.parse(value), null, 2);
      }
      return JSON.stringify(value, null, 2);
    } catch {
      return typeof value === "string" ? value : JSON.stringify(value, null, 2);
    }
  };

  const renderCountBadge = (count) => (
    count > 0 ? (
    <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">
      {count}
    </span>
    ) : null
  );

  const getAnalysisPayloadForTab = (item, analysisTab) => {
    const byCodeResult = item?.rxnorm_medications_by_code_result;
    const isByCodeTab = analysisTab === TAB_MEDICAL_ENTITY_BY_CODE;
    const analysisResult = isByCodeTab
      ? {}
      : (analysisTab === TAB_DIAGNOSIS_INFERENCE
        ? (item?.diagnosis_entities_analysis_result ?? {})
        : (item?.medical_entity_analysis_result ?? {}));
    let parsedAnalysis = {};
    try {
      if (isByCodeTab && byCodeResult != null) {
        parsedAnalysis = { comparison: getComparisonFromByCodeResult(byCodeResult) };
      } else {
        parsedAnalysis = typeof analysisResult === "string"
          ? JSON.parse(analysisResult)
          : (analysisResult || {});
      }
    } catch {
      parsedAnalysis = {};
    }
    return { analysisResult, parsedAnalysis };
  };

  const getAggregateTabCount = (analysisTab, includeCommon = true) => {
    if (!Array.isArray(results)) return 0;
    return results.reduce((sum, item) => {
      const { analysisResult, parsedAnalysis } = getAnalysisPayloadForTab(item, analysisTab);
      const flaggedTranscriptCount = (getFlaggedHighlights(analysisResult, "transcript", analysisTab) || []).length;
      const flaggedNoteCount = (getFlaggedHighlights(analysisResult, "note", analysisTab) || []).length;
      const commonCount = (getCommonItems(parsedAnalysis?.comparison) || []).length;
      const omissionCount = (getOmissionItems(parsedAnalysis?.comparison) || []).length;
      const hallucinationCount = (getHallucinationItems(parsedAnalysis?.comparison) || []).length;
      return sum + flaggedTranscriptCount + flaggedNoteCount + omissionCount + hallucinationCount + (includeCommon ? commonCount : 0);
    }, 0);
  };

  const medicalEntityTabCount = getAggregateTabCount(TAB_MEDICAL_ENTITY, true);
  const diagnosisTabCount = getAggregateTabCount(TAB_DIAGNOSIS_INFERENCE, true);
  const byCodeTabCount = getAggregateTabCount(TAB_MEDICAL_ENTITY_BY_CODE, true);
  const consumerEffectiveTab = consumerDataTab === "diagnosis" ? TAB_DIAGNOSIS_INFERENCE : TAB_MEDICAL_ENTITY;
  const consumerTabCount = getAggregateTabCount(consumerEffectiveTab, false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResults(null);
    setSelectedIndex(null);
    const trimmed = (patientId || "").trim();
    if (!trimmed) {
      setError("Please enter a patient id");
      return;
    }
    try {
      setIsLoading(true);
      const response = await customFetch(
        `${environment.apiUrl}/hallucination/medical-entities/by-patient/${encodeURIComponent(
          trimmed
        )}`,
        { method: "GET" }
      );
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }
      const data = await response.json();
      if (Array.isArray(data) && data.length === 0) {
        setError("Patient id not found.");
      } else {
        setResults(data);
      }
    } catch (err) {
      setError("Failed to fetch data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex">
      <div className="h-[100dvh]">
        <div className="flex h-[100%] fixed top-0">
          <Sidebar setAuth={setAuth} setUserOrg={setUserOrg} />
        </div>
      </div>
      <div
        className="pl-[10px] pr-[10px] flex flex-col w-full overflow-x-hidden"
        style={{
          marginLeft: "var(--sidebar-width, 250px)",
          width: "calc(100% - var(--sidebar-width, 250px))",
        }}
      >
        <div className="flex flex-col w-full">
          <div className="flex flex-col justify-center items-center w-full">
            <h1 className="font-bold text-[28px] text-ktx-purple mt-[2rem]">
              Hallucination
            </h1>
            <div className="w-full max-w-7xl mt-8">
              <Card>
                <CardContent>
                  <div className="flex border-b border-gray-200 mt-4">
                    <button
                      type="button"
                      onClick={() => setActiveTab(TAB_MEDICAL_ENTITY)}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        activeTab === TAB_MEDICAL_ENTITY
                          ? "border-ktx-purple text-ktx-purple"
                          : "border-transparent text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <span className="inline-flex items-center">
                        Medical Entity Extraction
                        {renderCountBadge(medicalEntityTabCount)}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab(TAB_DIAGNOSIS_INFERENCE)}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        activeTab === TAB_DIAGNOSIS_INFERENCE
                          ? "border-ktx-purple text-ktx-purple"
                          : "border-transparent text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <span className="inline-flex items-center">
                        Diagnosis Inference
                        {renderCountBadge(diagnosisTabCount)}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab(TAB_MEDICAL_ENTITY_BY_CODE)}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        activeTab === TAB_MEDICAL_ENTITY_BY_CODE
                          ? "border-ktx-purple text-ktx-purple"
                          : "border-transparent text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <span className="inline-flex items-center">
                        Medical Entity by Code
                        {renderCountBadge(byCodeTabCount)}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab(TAB_CONSUMER_HALLUCINATIONS)}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        activeTab === TAB_CONSUMER_HALLUCINATIONS
                          ? "border-ktx-purple text-ktx-purple"
                          : "border-transparent text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <span className="inline-flex items-center">
                        Consumer Hallucinations
                        {renderCountBadge(consumerTabCount)}
                      </span>
                    </button>
                  </div>
                  {activeTab === TAB_CONSUMER_HALLUCINATIONS && (
                    <div className="mt-4 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setConsumerDataTab("medications")}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                          consumerDataTab === "medications"
                            ? "bg-ktx-purple text-white border-ktx-purple"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        <span className="inline-flex items-center">
                          Medications
                          {renderCountBadge(getAggregateTabCount(TAB_MEDICAL_ENTITY, false))}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setConsumerDataTab("diagnosis")}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                          consumerDataTab === "diagnosis"
                            ? "bg-ktx-purple text-white border-ktx-purple"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        <span className="inline-flex items-center">
                          Diagnosis
                          {renderCountBadge(getAggregateTabCount(TAB_DIAGNOSIS_INFERENCE, false))}
                        </span>
                      </button>
                    </div>
                  )}
                  <form onSubmit={onSubmit} className="mt-4 flex items-end gap-3">
                    <div className="flex flex-col">
                      <label htmlFor="patientId" className="text-xs text-gray-600 mb-1">Patient ID</label>
                      <Input
                        id="patientId"
                        type="text"
                        value={patientId}
                        onChange={(e) => setPatientId(e.target.value)}
                        placeholder="Enter patient id..."
                        className="w-full md:w-[200px]"
                      />
                    </div>
                    <Button type="submit" disabled={isLoading} className="bg-ktx-purple hover:bg-purple-700">
                      {isLoading ? "Searching..." : "Submit"}
                    </Button>
                  </form>
                  {error && (
                    <div className="mt-4 text-sm text-red-600">{error}</div>
                  )}
                  {results && (
                    <div className="mt-4">
                      <div className="space-y-4">
                        {Array.isArray(results) && results.map((item, idx) => {
                          const summarize_job_name = item?.summarize_job_name ?? "N/A";
                          const transcript = item?.transcript ?? "";
                          const note = item?.note ?? "";
                          const isConsumerTab = activeTab === TAB_CONSUMER_HALLUCINATIONS;
                          const effectiveAnalysisTab = isConsumerTab
                            ? (consumerDataTab === "diagnosis" ? TAB_DIAGNOSIS_INFERENCE : TAB_MEDICAL_ENTITY)
                            : activeTab;
                          const medicationSuggestionsForTab = effectiveAnalysisTab === TAB_DIAGNOSIS_INFERENCE
                            ? undefined
                            : getMedicationSuggestions;
                          const isByCodeTab = effectiveAnalysisTab === TAB_MEDICAL_ENTITY_BY_CODE;
                          const byCodeResult = item?.rxnorm_medications_by_code_result;
                          const analysisResult = isByCodeTab
                            ? {}
                            : (effectiveAnalysisTab === TAB_DIAGNOSIS_INFERENCE
                              ? (item?.diagnosis_entities_analysis_result ?? {})
                              : (item?.medical_entity_analysis_result ?? {}));
                          let parsedAnalysis = {};
                          try {
                            if (isByCodeTab && byCodeResult != null) {
                              parsedAnalysis = { comparison: getComparisonFromByCodeResult(byCodeResult) };
                            } else {
                              parsedAnalysis = typeof analysisResult === "string"
                                ? JSON.parse(analysisResult)
                                : (analysisResult || {});
                            }
                          } catch {
                            parsedAnalysis = {};
                          }
                          const transcriptHighlightsForHighlightText = (() => {
                            const all = getFlaggedHighlights(analysisResult, "transcript", effectiveAnalysisTab) || [];
                            const focusedKey = isConsumerTab
                              && consumerFocusedGroup?.pane === "transcript"
                              && consumerFocusedGroup?.category === "missing"
                              ? consumerFocusedGroup.key
                              : null;
                            return focusedKey
                              ? all.filter(h => normalizeConsumerGroupKey(getFlaggedHighlightLabel(h)) === focusedKey)
                              : all;
                          })();
                          const transcriptOmissionItemsForHighlightText = (() => {
                            const all = getOmissionItems(parsedAnalysis?.comparison) || [];
                            const focusedKey = isConsumerTab
                              && consumerFocusedGroup?.pane === "transcript"
                              && consumerFocusedGroup?.category === "omission"
                              ? consumerFocusedGroup.key
                              : null;
                            return focusedKey
                              ? all.filter(it => normalizeConsumerGroupKey(it?.transcriptLabel) === focusedKey)
                              : all;
                          })();
                          const noteHighlightsForHighlightText = (() => {
                            const all = getFlaggedHighlights(analysisResult, "note", effectiveAnalysisTab) || [];
                            const focusedKey = isConsumerTab
                              && consumerFocusedGroup?.pane === "note"
                              && consumerFocusedGroup?.category === "missing"
                              ? consumerFocusedGroup.key
                              : null;
                            return focusedKey
                              ? all.filter(h => normalizeConsumerGroupKey(getFlaggedHighlightLabel(h)) === focusedKey)
                              : all;
                          })();
                          const noteHallucinationItemsForHighlightText = (() => {
                            const all = getHallucinationItems(parsedAnalysis?.comparison) || [];
                            const focusedKey = isConsumerTab
                              && consumerFocusedGroup?.pane === "note"
                              && consumerFocusedGroup?.category === "hallucination"
                              ? consumerFocusedGroup.key
                              : null;
                            return focusedKey
                              ? all.filter(it => normalizeConsumerGroupKey(it?.noteLabel) === focusedKey)
                              : all;
                          })();
                          const isOpen = selectedIndex === idx;
                          return (
                            <Card key={idx}>
                              <CardHeader>
                                <button
                                  type="button"
                                  onClick={() => setSelectedIndex(isOpen ? null : idx)}
                                  className="w-full flex items-center justify-between text-left"
                                >
                                  <CardTitle className="text-base break-all">
                                    {summarize_job_name}
                                  </CardTitle>
                                  <span className="text-xs text-gray-500 ml-4">
                                    {isOpen ? "Hide" : "Show"}
                                  </span>
                                </button>
                              </CardHeader>
                              {isOpen && (
                                <CardContent>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                                    <div className="w-full">
                                      <div className="text-lg font-bold text-ktx-purple mb-2">Transcript</div>
                                      {(() => {
                                        const flaggedHighlightsT = getFlaggedHighlights(analysisResult, "transcript", effectiveAnalysisTab) || [];
                                        const medItemsT = flaggedHighlightsT.map(h => ({
                                          label: getFlaggedHighlightLabel(h),
                                          start: h.start,
                                          end: h.end
                                        }));
                                        const isDiagnosisContext = effectiveAnalysisTab === TAB_DIAGNOSIS_INFERENCE;
                                        const { perfectMatchItems: medItemsTPerfectMatch, otherItems: medItemsTOther } = isDiagnosisContext
                                          ? { perfectMatchItems: [], otherItems: medItemsT }
                                          : splitFlaggedMedicationItems(medItemsT);
                                        const commonItems = getCommonItems(parsedAnalysis?.comparison) || [];
                                        const omissionItems = getOmissionItems(parsedAnalysis?.comparison) || [];
                                        const consumerPerfectGroupsT = isConsumerTab
                                          ? groupConsumerOccurrences(medItemsTPerfectMatch, it => it.label, it => it.start, it => it.end)
                                          : [];
                                        const consumerFlaggedGroupsT = isConsumerTab
                                          ? groupConsumerOccurrences((isDiagnosisContext ? medItemsT : medItemsTOther), it => it.label, it => it.start, it => it.end)
                                          : [];
                                        const consumerOmissionGroups = isConsumerTab
                                          ? groupConsumerOccurrences(omissionItems, it => it.transcriptLabel, it => it.transcriptStart, it => it.transcriptEnd)
                                          : [];
                                        const focusedTranscriptMissingKey = isConsumerTab
                                          && consumerFocusedGroup?.pane === "transcript"
                                          && consumerFocusedGroup?.category === "missing"
                                          ? consumerFocusedGroup.key
                                          : null;
                                        const focusedTranscriptOmissionKey = isConsumerTab
                                          && consumerFocusedGroup?.pane === "transcript"
                                          && consumerFocusedGroup?.category === "omission"
                                          ? consumerFocusedGroup.key
                                          : null;
                                        const transcriptHighlightsForView = focusedTranscriptMissingKey
                                          ? flaggedHighlightsT.filter(h => normalizeConsumerGroupKey(getFlaggedHighlightLabel(h)) === focusedTranscriptMissingKey)
                                          : flaggedHighlightsT;
                                        const transcriptOmissionItemsForView = focusedTranscriptOmissionKey
                                          ? omissionItems.filter(it => normalizeConsumerGroupKey(it?.transcriptLabel) === focusedTranscriptOmissionKey)
                                          : omissionItems;
                                        const showEntityConceptSplit = !isConsumerTab && (activeTab === TAB_MEDICAL_ENTITY || activeTab === TAB_DIAGNOSIS_INFERENCE || activeTab === TAB_MEDICAL_ENTITY_BY_CODE);
                                        const omissionLabel = isConsumerTab ? "Present in Transcript, Not in Note" : "Omissions";
                                        const entityCommonItems = commonItems.filter(it => it.kind === "entity");
                                        const conceptCommonItems = commonItems.filter(it => it.kind === "concept");
                                        const entityOmissionItems = omissionItems.filter(it => it.kind === "entity_omission");
                                        const conceptOmissionItems = omissionItems.filter(it => it.kind === "concept_omission");
                                        const rowCls = (type) => {
                                          if (layerSelection.transcript !== type) return "";
                                          if (type === "missing") return "bg-gray-100";
                                          if (type === "entity_common" || type === "concept_common" || type === "common") return "bg-green-100";
                                          if (type === "entity_omission" || type === "concept_omission" || type === "omission") return "bg-red-100";
                                          return "";
                                        };
                                        const renderCommonChips = (items, transcriptType, noteType) =>
                                          items.map((it, i) => (
                                            <button
                                              key={`t-com-${transcriptType}-${i}`}
                                              type="button"
                                              onClick={() => {
                                                setLayerSelection({ transcript: transcriptType, note: noteType });
                                                if (typeof it.transcriptStart === "number" && typeof it.transcriptEnd === "number") {
                                                  transcriptRef?.current?.scrollToRawRange(it.transcriptStart, it.transcriptEnd, false, "common");
                                                }
                                                if (typeof it.noteStart === "number" && typeof it.noteEnd === "number") {
                                                  noteRef?.current?.scrollToRawRange(it.noteStart, it.noteEnd, false, "common");
                                                }
                                              }}
                                              className="px-2 py-1 text-xs rounded-full bg-green-300 hover:bg-green-400 text-gray-900"
                                            >
                                              {it.transcriptLabel || it.noteLabel || `Item ${i + 1}`}
                                            </button>
                                          ));
                                        const renderOmissionChips = (items, transcriptType) =>
                                          items.map((it, i) => (
                                            <button
                                              key={`t-omit-${transcriptType}-${i}`}
                                              type="button"
                                              onClick={() => { setLayerSelection({ transcript: transcriptType, note: "hallucination" }); transcriptRef?.current?.scrollToRawRange(it.transcriptStart, it.transcriptEnd, false, "omission"); }}
                                              className="px-2 py-1 text-xs rounded-full bg-red-300 hover:bg-red-400 text-gray-900"
                                            >
                                              {it.transcriptLabel || `Omission ${i + 1}`}
                                            </button>
                                          ));
                                        const renderConsumerGroupChips = (groups, category, colorClass, onScrollOccurrence) =>
                                          groups.map((group) => {
                                            const stateKey = `${idx}|transcript|${category}|${group.key}`;
                                            const currentIndex = consumerOccurrenceIndexByGroup[stateKey] ?? 0;
                                            const count = group.occurrences.length;
                                            const safeIndex = count > 0 ? Math.min(currentIndex, count - 1) : 0;
                                            const gotoIndex = (nextIndex) => {
                                              if (count === 0) return;
                                              const wrapped = ((nextIndex % count) + count) % count;
                                              const occ = group.occurrences[wrapped];
                                              setLayerSelection({
                                                transcript: category === "omission" ? "omission" : "missing",
                                                note: category === "omission" ? "hallucination" : "missing"
                                              });
                                              setConsumerFocusedGroup({ pane: "transcript", category, key: group.key });
                                              setConsumerOccurrenceIndexByGroup(prev => ({ ...prev, [stateKey]: wrapped }));
                                              onScrollOccurrence(occ);
                                            };
                                            return (
                                              <div key={`${category}-${group.key}`} className="inline-flex items-center gap-1">
                                                {count > 1 && (
                                                  <button
                                                    type="button"
                                                    onClick={() => gotoIndex(safeIndex - 1)}
                                                    className="h-4 w-4 inline-flex items-center justify-center text-[10px] text-gray-700 hover:text-gray-900"
                                                  >
                                                    ◀
                                                  </button>
                                                )}
                                                <button
                                                  type="button"
                                                  onClick={() => gotoIndex(0)}
                                                  className={`px-2 py-1 text-xs rounded-full text-gray-900 ${colorClass}`}
                                                >
                                                  {group.label} {count > 1 ? `(${safeIndex + 1}/${count})` : ""}
                                                </button>
                                                {count > 1 && (
                                                  <button
                                                    type="button"
                                                    onClick={() => gotoIndex(safeIndex + 1)}
                                                    className="h-4 w-4 inline-flex items-center justify-center text-[10px] text-gray-700 hover:text-gray-900"
                                                  >
                                                    ▶
                                                  </button>
                                                )}
                                              </div>
                                            );
                                          });
                                        return (
                                          <div className="mb-3 overflow-x-auto min-h-40">
                                            <table className="min-w-full text-sm">
                                              <tbody className="align-top">
                                                {!isDiagnosisContext && (
                                                  <tr className={`border-t ${rowCls("missing")}`}>
                                                    <td className="py-2 pr-4">
                                                      <span className="inline-flex items-center">Flagged — Exact Suggested Match {renderCountBadge(isConsumerTab ? consumerPerfectGroupsT.length : medItemsTPerfectMatch.length)}</span>
                                                    </td>
                                                    <td className="py-2">
                                                      <div className="flex flex-wrap gap-2">
                                                        {isConsumerTab
                                                          ? renderConsumerGroupChips(
                                                            consumerPerfectGroupsT,
                                                            "missing",
                                                            "bg-blue-300 hover:bg-blue-400",
                                                            (occ) => transcriptRef?.current?.scrollToOffset(occ?.start ?? 0, true)
                                                          )
                                                          : medItemsTPerfectMatch.map((it, i) => (
                                                            <button
                                                              key={`t-perfect-${i}`}
                                                              type="button"
                                                              onClick={() => { setLayerSelection({ transcript: "missing", note: "missing" }); transcriptRef?.current?.scrollToOffset(it.start, true); }}
                                                              className="px-2 py-1 text-xs rounded-full bg-blue-300 hover:bg-blue-400 text-gray-900"
                                                            >
                                                              {it.label}
                                                            </button>
                                                          ))}
                                                      </div>
                                                    </td>
                                                  </tr>
                                                )}
                                                <tr className={`border-t ${rowCls("missing")}`}>
                                                  <td className="py-2 pr-4">
                                                    <span className="inline-flex items-center">Flagged {renderCountBadge(isConsumerTab ? consumerFlaggedGroupsT.length : (isDiagnosisContext ? medItemsT : medItemsTOther).length)}</span>
                                                  </td>
                                                  <td className="py-2">
                                                    <div className="flex flex-wrap gap-2">
                                                      {isConsumerTab
                                                        ? renderConsumerGroupChips(
                                                          consumerFlaggedGroupsT,
                                                          "missing",
                                                          "bg-gray-300 hover:bg-gray-400",
                                                          (occ) => transcriptRef?.current?.scrollToOffset(occ?.start ?? 0, true)
                                                        )
                                                        : (isDiagnosisContext ? medItemsT : medItemsTOther).map((it, i) => (
                                                          <button
                                                            key={`t-miss-${i}`}
                                                            type="button"
                                                            onClick={() => { setLayerSelection({ transcript: "missing", note: "missing" }); transcriptRef?.current?.scrollToOffset(it.start, true); }}
                                                            className="px-2 py-1 text-xs rounded-full bg-gray-300 hover:bg-gray-400 text-gray-900"
                                                          >
                                                            {it.label}
                                                          </button>
                                                        ))}
                                                    </div>
                                                  </td>
                                                </tr>
                                                {showEntityConceptSplit ? (
                                                  <>
                                                    <tr className={`border-t ${rowCls("entity_common")}`}>
                                                      <td className="py-2 pr-4">
                                                        <span className="inline-flex items-center">Entity — Common {renderCountBadge(entityCommonItems.length)}</span>
                                                      </td>
                                                      <td className="py-2"><div className="flex flex-wrap gap-2">{renderCommonChips(entityCommonItems, "entity_common", "entity_common")}</div></td>
                                                    </tr>
                                                    <tr className={`border-t ${rowCls("entity_omission")}`}>
                                                      <td className="py-2 pr-4">
                                                        <span className="inline-flex items-center">Entity — Omissions {renderCountBadge(entityOmissionItems.length)}</span>
                                                      </td>
                                                      <td className="py-2"><div className="flex flex-wrap gap-2">{renderOmissionChips(entityOmissionItems, "entity_omission")}</div></td>
                                                    </tr>
                                                    <tr className={`border-t ${rowCls("concept_common")}`}>
                                                      <td className="py-2 pr-4">
                                                        <span className="inline-flex items-center">Concept — Common {renderCountBadge(conceptCommonItems.length)}</span>
                                                      </td>
                                                      <td className="py-2"><div className="flex flex-wrap gap-2">{renderCommonChips(conceptCommonItems, "concept_common", "concept_common")}</div></td>
                                                    </tr>
                                                    <tr className={`border-t ${rowCls("concept_omission")}`}>
                                                      <td className="py-2 pr-4">
                                                        <span className="inline-flex items-center">Concept — Omissions {renderCountBadge(conceptOmissionItems.length)}</span>
                                                      </td>
                                                      <td className="py-2"><div className="flex flex-wrap gap-2">{renderOmissionChips(conceptOmissionItems, "concept_omission")}</div></td>
                                                    </tr>
                                                  </>
                                                ) : (
                                                  <>
                                                    {!isConsumerTab && (
                                                      <tr className={`border-t ${rowCls("common")}`}>
                                                        <td className="py-2 pr-4">
                                                          <span className="inline-flex items-center">Common {renderCountBadge(commonItems.length)}</span>
                                                        </td>
                                                        <td className="py-2"><div className="flex flex-wrap gap-2">{renderCommonChips(commonItems, "common", "common")}</div></td>
                                                      </tr>
                                                    )}
                                                    <tr className={`border-t ${rowCls("omission")}`}>
                                                      <td className="py-2 pr-4">
                                                        <span className="inline-flex items-center">{omissionLabel} {renderCountBadge(isConsumerTab ? consumerOmissionGroups.length : omissionItems.length)}</span>
                                                      </td>
                                                      <td className="py-2">
                                                        <div className="flex flex-wrap gap-2">
                                                          {isConsumerTab
                                                            ? renderConsumerGroupChips(
                                                              consumerOmissionGroups,
                                                              "omission",
                                                              "bg-red-300 hover:bg-red-400",
                                                              (occ) => transcriptRef?.current?.scrollToRawRange(occ?.start ?? 0, occ?.end ?? 0, false, "omission")
                                                            )
                                                            : renderOmissionChips(omissionItems, "omission")}
                                                        </div>
                                                      </td>
                                                    </tr>
                                                  </>
                                                )}
                                              </tbody>
                                            </table>
                                          </div>
                                        );
                                      })()}
                                      
                                      <HighlightText
                                        ref={transcriptRef}
                                        text={transcript || "—"}
                                        highlights={transcriptHighlightsForHighlightText}
                                        scrollBoxClassName="h-96"
                                        visibleLayer={normalizeVisibleLayer(layerSelection.transcript)}
                                        getMedicationSuggestions={medicationSuggestionsForTab}
                                        commonRanges={(getCommonItems(parsedAnalysis?.comparison) || []).map(it => (
                                          typeof it.transcriptStart === "number" && typeof it.transcriptEnd === "number"
                                            ? { start: it.transcriptStart, end: it.transcriptEnd, data: { kind: it.kind, raw: it.raw, pane: "transcript" } }
                                            : null
                                        )).filter(Boolean)}
                                        omissionRanges={transcriptOmissionItemsForHighlightText.map(it => ({
                                          start: it.transcriptStart,
                                          end: it.transcriptEnd,
                                          data: { kind: it.kind, raw: it.raw, pane: "transcript" }
                                        }))}
                                        showHighlightChips={false}
                                        showCommonChips={false}
                                        showOmissionChips={false}
                                        showHallucinationChips={false}
                                      />
                                    </div>
                                    
                                    <div className="w-full">
                                      <div className="text-lg font-bold text-ktx-purple mb-2">Note</div>
                                      {(() => {
                                        const flaggedHighlightsN = getFlaggedHighlights(analysisResult, "note", effectiveAnalysisTab) || [];
                                        const medItemsN = flaggedHighlightsN.map(h => ({
                                          label: getFlaggedHighlightLabel(h),
                                          start: h.start,
                                          end: h.end
                                        }));
                                        const isDiagnosisContext = effectiveAnalysisTab === TAB_DIAGNOSIS_INFERENCE;
                                        const { perfectMatchItems: medItemsNPerfectMatch, otherItems: medItemsNOther } = isDiagnosisContext
                                          ? { perfectMatchItems: [], otherItems: medItemsN }
                                          : splitFlaggedMedicationItems(medItemsN);
                                        const commonItemsN = getCommonItems(parsedAnalysis?.comparison) || [];
                                        const hallucinationItems = getHallucinationItems(parsedAnalysis?.comparison) || [];
                                        const consumerPerfectGroupsN = isConsumerTab
                                          ? groupConsumerOccurrences(medItemsNPerfectMatch, it => it.label, it => it.start, it => it.end)
                                          : [];
                                        const consumerFlaggedGroupsN = isConsumerTab
                                          ? groupConsumerOccurrences((isDiagnosisContext ? medItemsN : medItemsNOther), it => it.label, it => it.start, it => it.end)
                                          : [];
                                        const consumerHallucinationGroups = isConsumerTab
                                          ? groupConsumerOccurrences(hallucinationItems, it => it.noteLabel, it => it.noteStart, it => it.noteEnd)
                                          : [];
                                        const focusedNoteMissingKey = isConsumerTab
                                          && consumerFocusedGroup?.pane === "note"
                                          && consumerFocusedGroup?.category === "missing"
                                          ? consumerFocusedGroup.key
                                          : null;
                                        const focusedNoteHallucinationKey = isConsumerTab
                                          && consumerFocusedGroup?.pane === "note"
                                          && consumerFocusedGroup?.category === "hallucination"
                                          ? consumerFocusedGroup.key
                                          : null;
                                        const noteHighlightsForView = focusedNoteMissingKey
                                          ? flaggedHighlightsN.filter(h => normalizeConsumerGroupKey(getFlaggedHighlightLabel(h)) === focusedNoteMissingKey)
                                          : flaggedHighlightsN;
                                        const noteHallucinationItemsForView = focusedNoteHallucinationKey
                                          ? hallucinationItems.filter(it => normalizeConsumerGroupKey(it?.noteLabel) === focusedNoteHallucinationKey)
                                          : hallucinationItems;
                                        const showEntityConceptSplitN = !isConsumerTab && (activeTab === TAB_MEDICAL_ENTITY || activeTab === TAB_DIAGNOSIS_INFERENCE || activeTab === TAB_MEDICAL_ENTITY_BY_CODE);
                                        const hallucinationLabel = isConsumerTab ? "Present in Note, Not in Transcript" : "Hallucinations";
                                        const entityCommonItemsN = commonItemsN.filter(it => it.kind === "entity");
                                        const conceptCommonItemsN = commonItemsN.filter(it => it.kind === "concept");
                                        const entityHallucinationItems = hallucinationItems.filter(it => it.kind === "entity_hallucination");
                                        const conceptHallucinationItems = hallucinationItems.filter(it => it.kind === "concept_hallucination");
                                        const rowClsN = (type) => {
                                          if (layerSelection.note !== type) return "";
                                          if (type === "missing") return "bg-gray-100";
                                          if (type === "entity_common" || type === "concept_common" || type === "common") return "bg-green-100";
                                          if (type === "entity_hallucination" || type === "concept_hallucination" || type === "hallucination") return "bg-red-100";
                                          return "";
                                        };
                                        const renderCommonChipsN = (items, transcriptType, noteType) =>
                                          items.map((it, i) => (
                                            <button
                                              key={`n-com-${noteType}-${i}`}
                                              type="button"
                                              onClick={() => {
                                                setLayerSelection({ transcript: transcriptType, note: noteType });
                                                if (typeof it.transcriptStart === "number" && typeof it.transcriptEnd === "number") {
                                                  transcriptRef?.current?.scrollToRawRange(it.transcriptStart, it.transcriptEnd, false, "common");
                                                }
                                                if (typeof it.noteStart === "number" && typeof it.noteEnd === "number") {
                                                  noteRef?.current?.scrollToRawRange(it.noteStart, it.noteEnd, false, "common");
                                                }
                                              }}
                                              className="px-2 py-1 text-xs rounded-full bg-green-300 hover:bg-green-400 text-gray-900"
                                            >
                                              {it.transcriptLabel || it.noteLabel || `Item ${i + 1}`}
                                            </button>
                                          ));
                                        const renderHallucinationChips = (items, noteType) =>
                                          items.map((it, i) => (
                                            <button
                                              key={`n-hall-${noteType}-${i}`}
                                              type="button"
                                              onClick={() => { setLayerSelection({ transcript: "omission", note: noteType }); noteRef?.current?.scrollToRawRange(it.noteStart, it.noteEnd, false, "hallucination"); }}
                                              className="px-2 py-1 text-xs rounded-full bg-red-300 hover:bg-red-400 text-gray-900"
                                            >
                                              {it.noteLabel || `Hallucination ${i + 1}`}
                                            </button>
                                          ));
                                        const renderConsumerGroupChipsN = (groups, category, colorClass, onScrollOccurrence) =>
                                          groups.map((group) => {
                                            const stateKey = `${idx}|note|${category}|${group.key}`;
                                            const currentIndex = consumerOccurrenceIndexByGroup[stateKey] ?? 0;
                                            const count = group.occurrences.length;
                                            const safeIndex = count > 0 ? Math.min(currentIndex, count - 1) : 0;
                                            const gotoIndex = (nextIndex) => {
                                              if (count === 0) return;
                                              const wrapped = ((nextIndex % count) + count) % count;
                                              const occ = group.occurrences[wrapped];
                                              setLayerSelection({
                                                transcript: category === "hallucination" ? "omission" : "missing",
                                                note: category === "hallucination" ? "hallucination" : "missing"
                                              });
                                              setConsumerFocusedGroup({ pane: "note", category, key: group.key });
                                              setConsumerOccurrenceIndexByGroup(prev => ({ ...prev, [stateKey]: wrapped }));
                                              onScrollOccurrence(occ);
                                            };
                                            return (
                                              <div key={`${category}-${group.key}`} className="inline-flex items-center gap-1">
                                                {count > 1 && (
                                                  <button
                                                    type="button"
                                                    onClick={() => gotoIndex(safeIndex - 1)}
                                                    className="h-4 w-4 inline-flex items-center justify-center text-[10px] text-gray-700 hover:text-gray-900"
                                                  >
                                                    ◀
                                                  </button>
                                                )}
                                                <button
                                                  type="button"
                                                  onClick={() => gotoIndex(0)}
                                                  className={`px-2 py-1 text-xs rounded-full text-gray-900 ${colorClass}`}
                                                >
                                                  {group.label} {count > 1 ? `(${safeIndex + 1}/${count})` : ""}
                                                </button>
                                                {count > 1 && (
                                                  <button
                                                    type="button"
                                                    onClick={() => gotoIndex(safeIndex + 1)}
                                                    className="h-4 w-4 inline-flex items-center justify-center text-[10px] text-gray-700 hover:text-gray-900"
                                                  >
                                                    ▶
                                                  </button>
                                                )}
                                              </div>
                                            );
                                          });
                                        return (
                                          <div className="mb-3 overflow-x-auto min-h-40">
                                            <table className="min-w-full text-sm">
                                              <tbody className="align-top">
                                                {!isDiagnosisContext && (
                                                  <tr className={`border-t ${rowClsN("missing")}`}>
                                                    <td className="py-2 pr-4">
                                                      <span className="inline-flex items-center">Flagged — Exact Suggested Match {renderCountBadge(isConsumerTab ? consumerPerfectGroupsN.length : medItemsNPerfectMatch.length)}</span>
                                                    </td>
                                                    <td className="py-2">
                                                      <div className="flex flex-wrap gap-2">
                                                        {isConsumerTab
                                                          ? renderConsumerGroupChipsN(
                                                            consumerPerfectGroupsN,
                                                            "missing",
                                                            "bg-blue-300 hover:bg-blue-400",
                                                            (occ) => noteRef?.current?.scrollToOffset(occ?.start ?? 0, true)
                                                          )
                                                          : medItemsNPerfectMatch.map((it, i) => (
                                                            <button
                                                              key={`n-perfect-${i}`}
                                                              type="button"
                                                              onClick={() => { setLayerSelection({ transcript: "missing", note: "missing" }); noteRef?.current?.scrollToOffset(it.start, true); }}
                                                              className="px-2 py-1 text-xs rounded-full bg-blue-300 hover:bg-blue-400 text-gray-900"
                                                            >
                                                              {it.label}
                                                            </button>
                                                          ))}
                                                      </div>
                                                    </td>
                                                  </tr>
                                                )}
                                                <tr className={`border-t ${rowClsN("missing")}`}>
                                                  <td className="py-2 pr-4">
                                                    <span className="inline-flex items-center">Flagged {renderCountBadge(isConsumerTab ? consumerFlaggedGroupsN.length : (isDiagnosisContext ? medItemsN : medItemsNOther).length)}</span>
                                                  </td>
                                                  <td className="py-2">
                                                    <div className="flex flex-wrap gap-2">
                                                      {isConsumerTab
                                                        ? renderConsumerGroupChipsN(
                                                          consumerFlaggedGroupsN,
                                                          "missing",
                                                          "bg-gray-300 hover:bg-gray-400",
                                                          (occ) => noteRef?.current?.scrollToOffset(occ?.start ?? 0, true)
                                                        )
                                                        : (isDiagnosisContext ? medItemsN : medItemsNOther).map((it, i) => (
                                                          <button
                                                            key={`n-miss-${i}`}
                                                            type="button"
                                                            onClick={() => { setLayerSelection({ transcript: "missing", note: "missing" }); noteRef?.current?.scrollToOffset(it.start, true); }}
                                                            className="px-2 py-1 text-xs rounded-full bg-gray-300 hover:bg-gray-400 text-gray-900"
                                                          >
                                                            {it.label}
                                                          </button>
                                                        ))}
                                                    </div>
                                                  </td>
                                                </tr>
                                                {showEntityConceptSplitN ? (
                                                  <>
                                                    <tr className={`border-t ${rowClsN("entity_common")}`}>
                                                      <td className="py-2 pr-4">
                                                        <span className="inline-flex items-center">Entity — Common {renderCountBadge(entityCommonItemsN.length)}</span>
                                                      </td>
                                                      <td className="py-2"><div className="flex flex-wrap gap-2">{renderCommonChipsN(entityCommonItemsN, "entity_common", "entity_common")}</div></td>
                                                    </tr>
                                                    <tr className={`border-t ${rowClsN("entity_hallucination")}`}>
                                                      <td className="py-2 pr-4">
                                                        <span className="inline-flex items-center">Entity — Hallucinations {renderCountBadge(entityHallucinationItems.length)}</span>
                                                      </td>
                                                      <td className="py-2"><div className="flex flex-wrap gap-2">{renderHallucinationChips(entityHallucinationItems, "entity_hallucination")}</div></td>
                                                    </tr>
                                                    <tr className={`border-t ${rowClsN("concept_common")}`}>
                                                      <td className="py-2 pr-4">
                                                        <span className="inline-flex items-center">Concept — Common {renderCountBadge(conceptCommonItemsN.length)}</span>
                                                      </td>
                                                      <td className="py-2"><div className="flex flex-wrap gap-2">{renderCommonChipsN(conceptCommonItemsN, "concept_common", "concept_common")}</div></td>
                                                    </tr>
                                                    <tr className={`border-t ${rowClsN("concept_hallucination")}`}>
                                                      <td className="py-2 pr-4">
                                                        <span className="inline-flex items-center">Concept — Hallucinations {renderCountBadge(conceptHallucinationItems.length)}</span>
                                                      </td>
                                                      <td className="py-2"><div className="flex flex-wrap gap-2">{renderHallucinationChips(conceptHallucinationItems, "concept_hallucination")}</div></td>
                                                    </tr>
                                                  </>
                                                ) : (
                                                  <>
                                                    {!isConsumerTab && (
                                                      <tr className={`border-t ${rowClsN("common")}`}>
                                                        <td className="py-2 pr-4">
                                                          <span className="inline-flex items-center">Common {renderCountBadge(commonItemsN.length)}</span>
                                                        </td>
                                                        <td className="py-2"><div className="flex flex-wrap gap-2">{renderCommonChipsN(commonItemsN, "common", "common")}</div></td>
                                                      </tr>
                                                    )}
                                                    <tr className={`border-t ${rowClsN("hallucination")}`}>
                                                      <td className="py-2 pr-4">
                                                        <span className="inline-flex items-center">{hallucinationLabel} {renderCountBadge(isConsumerTab ? consumerHallucinationGroups.length : hallucinationItems.length)}</span>
                                                      </td>
                                                      <td className="py-2">
                                                        <div className="flex flex-wrap gap-2">
                                                          {isConsumerTab
                                                            ? renderConsumerGroupChipsN(
                                                              consumerHallucinationGroups,
                                                              "hallucination",
                                                              "bg-red-300 hover:bg-red-400",
                                                              (occ) => noteRef?.current?.scrollToRawRange(occ?.start ?? 0, occ?.end ?? 0, false, "hallucination")
                                                            )
                                                            : renderHallucinationChips(hallucinationItems, "hallucination")}
                                                        </div>
                                                      </td>
                                                    </tr>
                                                  </>
                                                )}
                                              </tbody>
                                            </table>
                                          </div>
                                        );
                                      })()}
                                      <HighlightText
                                        ref={noteRef}
                                        text={note || "—"}
                                        highlights={noteHighlightsForHighlightText}
                                        scrollBoxClassName="h-96"
                                        visibleLayer={normalizeVisibleLayer(layerSelection.note)}
                                        getMedicationSuggestions={medicationSuggestionsForTab}
                                        commonRanges={(getCommonItems(parsedAnalysis?.comparison) || []).map(it => (
                                          typeof it.noteStart === "number" && typeof it.noteEnd === "number"
                                            ? { start: it.noteStart, end: it.noteEnd, data: { kind: it.kind, raw: it.raw, pane: "note" } }
                                            : null
                                        )).filter(Boolean)}
                                        hallucinationRanges={noteHallucinationItemsForHighlightText.map(it => ({
                                          start: it.noteStart,
                                          end: it.noteEnd,
                                          data: { kind: it.kind, raw: it.raw, pane: "note" }
                                        }))}
                                        showHighlightChips={false}
                                        showCommonChips={false}
                                        showOmissionChips={false}
                                        showHallucinationChips={false}
                                      />
                                    </div>
                                  </div>
                                </CardContent>
                              )}
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hallucination;


